import { useEffect, useMemo, useState } from 'react';

import { api } from '../../services/api';
import { isInCalendarByProgram } from '../../components/dashboard/programs/programLive';
import type { LiveAudienceTier, LiveStatus } from '../../types/auth';
import { calendarMonthOf, canShareLiveLink } from './liveShareGate';

/**
 * CORE-879 — «Поделиться» у Live-сессии, которая у человека по программе.
 * Правило — `canShareLiveLink`; здесь только добыча двух его фактов.
 */

const GUEST_PROBE_TTL_MS = 60_000;
const guestProbes = new Map<string, { at: number; result: Promise<boolean> }>();

/**
 * Ответ сервера «откроется ли ссылка у гостя», один на ссылку в минуту:
 * карточки «Календаря» и страница сессии спрашивают об одном и том же.
 * Минута — чтобы правка видимости мастером не застревала до перезагрузки.
 *
 * Держится только ответ сервера про доступ. Сбой сети или 5xx для этого
 * вызова — «не откроется», но из кеша уходит: следующий спросит заново, а
 * не прячет кнопку до конца минуты (замечание ревью 23.10).
 */
export function liveOpensForGuestCached(
  liveId: number,
  inviteCode?: string | null,
  previewToken?: string | null,
  now: number = Date.now(),
): Promise<boolean> {
  const key = `${liveId}|${inviteCode ?? ''}|${previewToken ?? ''}`;
  const hit = guestProbes.get(key);
  if (hit && now - hit.at < GUEST_PROBE_TTL_MS) return hit.result;
  const entry: { at: number; result: Promise<boolean> } = { at: now, result: Promise.resolve(false) };
  entry.result = api.liveGuestAccess(liveId, previewToken, inviteCode).then(access => {
    if (access === 'unknown' && guestProbes.get(key) === entry) guestProbes.delete(key);
    return access === 'open';
  });
  guestProbes.set(key, entry);
  return entry.result;
}

/** Для тестов. */
export function resetLiveGuestProbes(): void {
  guestProbes.clear();
}

interface LiveShareGateArgs {
  liveId: number;
  status: LiveStatus | null | undefined;
  startDateTimeUtc: string | null | undefined;
  isOwner: boolean;
  ownerVisibility: LiveAudienceTier | null;
  isAuthenticated: boolean;
  /** Личный календарь из CalendarContext — только будущие события. */
  calendarItems: ReadonlyArray<{ id: number; byProgram?: boolean | null }>;
  /** То, что уйдёт в ссылке: код приглашения и токен соведущего. */
  inviteCode?: string | null;
  previewToken?: string | null;
}

/** Страница сессии: можно ли показывать «Поделиться», «Пригласить» и репост. */
export function useLiveShareGate({
  liveId,
  status,
  startDateTimeUtc,
  isOwner,
  ownerVisibility,
  isAuthenticated,
  calendarItems,
  inviteCode,
  previewToken,
}: LiveShareGateArgs): boolean {
  const viewer = isAuthenticated && !isOwner;
  const inUpcomingCalendar = viewer && isInCalendarByProgram(calendarItems, liveId);

  // Общий календарь приложения держит только будущее: как только сессия
  // началась, её там нет, а «Пригласить» в шапке идущей и «Поделиться» у
  // записи остаются. Для них — месяц личного календаря, где сессия лежит.
  const month = calendarMonthOf(startDateTimeUtc);
  const needsMonth = viewer && !inUpcomingCalendar && !!status && status !== 'upcoming' && !!month;
  const [inMonthCalendar, setInMonthCalendar] = useState(false);
  useEffect(() => {
    setInMonthCalendar(false);
    if (!needsMonth || !month) return;
    let cancelled = false;
    api.getUserCalendar({ month })
      .then(list => { if (!cancelled) setInMonthCalendar(isInCalendarByProgram(list, liveId)); })
      .catch(() => { /* не узнали — ведём себя как с сессией не из программы */ });
    return () => { cancelled = true; };
  }, [needsMonth, month, liveId]);

  const byProgram = inUpcomingCalendar || (needsMonth && inMonthCalendar);

  const [opensForGuest, setOpensForGuest] = useState<boolean | null>(null);
  useEffect(() => {
    setOpensForGuest(null);
    if (!byProgram) return;
    let cancelled = false;
    void liveOpensForGuestCached(liveId, inviteCode, previewToken)
      .then(ok => { if (!cancelled) setOpensForGuest(ok); });
    return () => { cancelled = true; };
  }, [byProgram, liveId, inviteCode, previewToken]);

  return canShareLiveLink({ isOwner, ownerVisibility, byProgram, opensForGuest });
}

/**
 * «Календарь»: какие из сессий программы открываются у гостя. Остальным
 * карточкам программы делиться нечем. Карточки не из программы сюда не
 * передают — у них кнопка как была.
 */
export function useLivesOpenForGuest(programLiveIds: readonly number[]): ReadonlySet<number> {
  const key = useMemo(
    () => Array.from(new Set(programLiveIds)).sort((a, b) => a - b).join(','),
    [programLiveIds],
  );
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
  useEffect(() => {
    if (!key) { setOpen(new Set()); return; }
    let cancelled = false;
    const ids = key.split(',').map(Number);
    void Promise.all(ids.map(id => liveOpensForGuestCached(id).then(ok => (ok ? id : null))))
      .then(results => {
        if (!cancelled) setOpen(new Set(results.filter((id): id is number => id != null)));
      });
    return () => { cancelled = true; };
  }, [key]);
  return open;
}
